// src/pages/Estatisticas/components/UsoIAPorClienteStats.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ExportarUsoIADialog from './ExportarUsoIADialog';

const OPERACAO_LABEL = {
  chat_ia: 'Chat com IA',
  gerar_documento: 'Gerar Documento',
  analisar_caso: 'Analisar Caso'
};

const COLUNAS = [
  { key: 'userEmail',         label: 'Usuário' },
  { key: 'operacao',          label: 'Operação com IA' },
  { key: 'codCli',            label: 'Cód. Cliente' },
  { key: 'totalAcoes',        label: 'Total Ações' },
  { key: 'tokensEstimados',   label: 'Tokens Estimados' },
  { key: 'primeiroRegistro',  label: 'Primeiro Registro' },
  { key: 'ultimoRegistro',    label: 'Último Registro' }
];

const formatarData = (dataString) => {
  if (!dataString) return '—';
  return new Date(dataString).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const formatarNumero = (n) => Number(n).toLocaleString('pt-BR');

const UsoIAPorClienteStats = () => {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortKey, setSortKey] = useState('tokensEstimados');
  const [sortAsc, setSortAsc] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);

  useEffect(() => { carregar(); }, []);

  const carregar = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/api/estatisticas/uso-ia-por-cliente');
      if (response.data.success) {
        setDados(response.data.data);
      } else {
        setError(response.data.message || 'Erro ao carregar dados');
      }
    } catch (err) {
      setError('Erro ao carregar: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortAsc(prev => !prev);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const dadosOrdenados = [...dados]
    .map(d => ({ ...d, operacao: OPERACAO_LABEL[d.nomeTabelaOriginal] || d.nomeTabelaOriginal }))
    .sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (va == null) return 1;
      if (vb == null) return -1;
      const cmp = typeof va === 'number'
        ? va - vb
        : String(va).localeCompare(String(vb), 'pt-BR');
      return sortAsc ? cmp : -cmp;
    });

  const totalAcoes = dados.reduce((s, d) => s + d.totalAcoes, 0);
  const totalTokens = dados.reduce((s, d) => s + d.tokensEstimados, 0);
  const clientesUnicos = new Set(dados.map(d => d.codCli)).size;

  const SortIcon = ({ colKey }) => {
    if (sortKey !== colKey) return <span style={{ opacity: 0.3, marginLeft: 4 }}>↕</span>;
    return <span style={{ marginLeft: 4 }}>{sortAsc ? '↑' : '↓'}</span>;
  };

  const TituloComExportar = () => (
    <div className="stat-box-title-row">
      <h3>🤖 Uso de IA por Cliente</h3>
      <button className="btn-exportar-dados" onClick={() => setShowExportDialog(true)}>
        EXPORTAR DADOS
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="stat-box">
        <TituloComExportar />
        <div className="loading-stats">
          <span>⏳</span>
          <span>Carregando dados...</span>
        </div>
        {showExportDialog && <ExportarUsoIADialog onClose={() => setShowExportDialog(false)} />}
      </div>
    );
  }

  if (error) {
    return (
      <div className="stat-box">
        <TituloComExportar />
        <div className="error-stats">{error}</div>
        {showExportDialog && <ExportarUsoIADialog onClose={() => setShowExportDialog(false)} />}
      </div>
    );
  }

  if (dados.length === 0) {
    return (
      <div className="stat-box">
        <TituloComExportar />
        <div className="no-stats">
          <span>📊</span>
          <span>Nenhum registro encontrado</span>
        </div>
        {showExportDialog && <ExportarUsoIADialog onClose={() => setShowExportDialog(false)} />}
      </div>
    );
  }

  return (
    <div className="stat-box">
      <TituloComExportar />

      <div className="access-summary-header" style={{ background: 'linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)' }}>
        <div className="summary-item">
          <span className="summary-label">Total de Ações</span>
          <span className="summary-value">{formatarNumero(totalAcoes)}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Tokens Estimados</span>
          <span className="summary-value">{formatarNumero(totalTokens)}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Clientes Únicos</span>
          <span className="summary-value">{clientesUnicos}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Registros</span>
          <span className="summary-value">{dados.length}</span>
        </div>
      </div>

      <div className="stats-table" style={{ marginTop: 20 }}>
        <div className="uso-ia-table-header">
          {COLUNAS.map(col => (
            <div
              key={col.key}
              className="uso-ia-cell uso-ia-cell-header"
              onClick={() => handleSort(col.key)}
            >
              {col.label}<SortIcon colKey={col.key} />
            </div>
          ))}
        </div>

        {dadosOrdenados.map((row, i) => (
          <div key={i} className="uso-ia-table-row">
            <div className="uso-ia-cell uso-ia-email">{row.userEmail}</div>
            <div className="uso-ia-cell uso-ia-operacao">{row.operacao}</div>
            <div className="uso-ia-cell uso-ia-codcli">{row.codCli}</div>
            <div className="uso-ia-cell uso-ia-numero">{formatarNumero(row.totalAcoes)}</div>
            <div className="uso-ia-cell uso-ia-tokens">{formatarNumero(row.tokensEstimados)}</div>
            <div className="uso-ia-cell uso-ia-data">{formatarData(row.primeiroRegistro)}</div>
            <div className="uso-ia-cell uso-ia-data">{formatarData(row.ultimoRegistro)}</div>
          </div>
        ))}
      </div>

      <div className="stats-summary">
        <p>Clique em qualquer cabeçalho para ordenar. Inclui apenas ações com código de cliente informado.</p>
      </div>

      {showExportDialog && <ExportarUsoIADialog onClose={() => setShowExportDialog(false)} />}
    </div>
  );
};

export default UsoIAPorClienteStats;
